import React from "react";
import { useNavigate } from "react-router-dom";
import { useDashboard } from "hooks/useModeration";
import { useTranslation } from "context/TranslationProvider";
import {
  Card,
  CardBody,
  CardTitle,
  CardHeader,
  Container,
  Row,
  Col,
  Button,
  Badge,
  Spinner,
  Alert,
  ListGroup,
  ListGroupItem,
  Progress,
  UncontrolledTooltip
} from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader";
import helpers from "utils/helpers";

function ModerationDashboard() {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const { stats, loading, error, refresh } = useDashboard();

  if (loading) {
    return (
      <div className="text-center py-5" style={{ minHeight: "300px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
        <Spinner color="primary" />
        <p className="mt-2 text-muted">{t("common.loading", "Loading moderation dashboard...")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert color="danger" className="shadow border-0">
          <strong>{t("common.error", "Error:")}</strong> {error}
          <div className="mt-3">
            <Button color="secondary" size="sm" onClick={refresh}>
              {t("common.retry", "Retry")}
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  // Calculate percentages for behavioral breakdown
  const criticalCount = stats.criticalBehavioral || 0;
  const highCount = stats.highBehavioral || 0;
  const mediumCount = stats.mediumBehavioral || 0;
  const behavioralTotal = criticalCount + highCount + mediumCount;
  
  const criticalPercent = behavioralTotal > 0 ? Math.round((criticalCount / behavioralTotal) * 100) : 0;
  const highPercent = behavioralTotal > 0 ? Math.round((highCount / behavioralTotal) * 100) : 0;
  const mediumPercent = behavioralTotal > 0 ? Math.round((mediumCount / behavioralTotal) * 100) : 0;

  return (
    <>
      <SimpleHeader name={t("sideNav.moderation", "Moderation")} parentName={t("page.flagged.subTitle", "Dashboard")} />
      
      <Container className="mt--6" fluid>
        <Row className="align-items-stretch">
          {/* Card 1: Verification */}
          <Col lg="4" className="mb-4 d-flex">
            <Card className="card-stats w-100 shadow border-0 card-hover-effect">
              <CardBody className="d-flex flex-column justify-content-between">
                <div>
                  <Row>
                    <div className="col">
                      <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
                        {t("categories.moderation.verificationQueue", "Verification Queue")}
                      </CardTitle>
                      <span className="h2 font-weight-bold mb-0">
                        {stats.activeVerification || 0}
                      </span>
                    </div>
                    <Col className="col-auto">
                      <div className="icon icon-shape bg-gradient-info text-white rounded-circle shadow">
                        <i className="fa fa-id-card" />
                      </div>
                    </Col>
                  </Row>
                  
                  <div className="mt-3 text-sm">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="text-muted font-weight-600">{t("categories.moderation.pendingReview", "Pending Review:")}</span>
                      <Badge color="info" className="badge-md font-weight-700">{stats.verificationPendingReview || 0}</Badge>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="text-muted font-weight-600">{t("categories.moderation.pendingUser", "Waiting on User:")}</span>
                      <Badge color="secondary" className="badge-md font-weight-700">{stats.verificationPending || 0}</Badge>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="text-muted font-weight-600">{t("categories.moderation.expiringSoon", "Expiring Soon:")}</span>
                      <Badge color="warning" className="badge-md font-weight-700">{stats.verificationExpiringSoon || 0}</Badge>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted font-weight-600">{t("categories.moderation.withinGracePeriod", "Within Grace Period:")}</span>
                      <Badge color="success" className="badge-md font-weight-700">{stats.verificationGraceActive || 0}</Badge>
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <Button
                    id="view-verification-queue"
                    color="info"
                    size="sm"
                    className="w-100 btn-icon"
                    title={t("categories.moderation.tooltip.viewVerificationQueue", "View verification moderation queue")}
                    onClick={() => navigate("/admin/moderation/verification")}
                  >
                    <span>{t("categories.moderation.viewQueue", "→ View Queue")}</span>
                  </Button>
                  <UncontrolledTooltip target="view-verification-queue" key={`viewVerificationQueue-tt-${language}`} placement="top" delay={0} container="body">
                    {t("categories.moderation.tooltip.viewVerificationQueue", "View verification moderation queue")}
                  </UncontrolledTooltip>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Card 2: Behavioral Priority Breakdown */}
          <Col lg="4" className="mb-4 d-flex">
            <Card className="card-stats w-100 shadow border-0 card-hover-effect">
              <CardBody className="d-flex flex-column justify-content-between">
                <div>
                  <Row>
                    <div className="col">
                      <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
                        {t("categories.moderation.behavioral", "Behavioral & Disputes")}
                      </CardTitle>
                      <span className="h2 font-weight-bold mb-0">
                        {stats.activeBehavioral || 0}
                      </span>
                    </div>
                    <Col className="col-auto">
                      <div className="icon icon-shape bg-gradient-danger text-white rounded-circle shadow">
                        <i className="fa fa-exclamation-triangle" />
                      </div>
                    </Col>
                  </Row>
                  
                  {/* UI Priority Breakdown Progress Bars */}
                  <div className="mt-3 text-xs">
                    <div className="mb-2">
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted font-weight-600 text-danger">{t("categories.moderation.criticalColon", "Critical:")} {criticalCount}</span>
                        <span className="font-weight-700 text-dark">{criticalPercent}%</span>
                      </div>
                      <Progress max="100" value={criticalPercent} color="danger" style={{ height: "6px" }} />
                    </div>
                    
                    <div className="mb-2">
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted font-weight-600 text-warning">{t("categories.moderation.highColon", "High:")} {highCount}</span>
                        <span className="font-weight-700 text-dark">{highPercent}%</span>
                      </div>
                      <Progress max="100" value={highPercent} color="warning" style={{ height: "6px" }} />
                    </div>

                    <div>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted font-weight-600 text-info">{t("categories.moderation.mediumColon", "Medium:")} {mediumCount}</span>
                        <span className="font-weight-700 text-dark">{mediumPercent}%</span>
                      </div>
                      <Progress max="100" value={mediumPercent} color="info" style={{ height: "6px" }} />
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <Button
                    id="view-behavioral-queue"
                    color="danger"
                    size="sm"
                    className="w-100"
                    title={t("categories.moderation.tooltip.viewBehavioralQueue", "View behavioral moderation queue")}
                    onClick={() => navigate("/admin/moderation/behavioral")}
                  >
                    <span>{t("categories.moderation.viewQueue", "→ View Queue")}</span>
                  </Button>
                  <UncontrolledTooltip target="view-behavioral-queue" key={`viewBehavioralQueue-tt-${language}`} placement="top" delay={0} container="body">
                    {t("categories.moderation.tooltip.viewBehavioralQueue", "View behavioral moderation queue")}
                  </UncontrolledTooltip>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Card 3: Appeals */}
          <Col lg="4" className="mb-4 d-flex">
            <Card className="card-stats w-100 shadow border-0 card-hover-effect">
              <CardBody className="d-flex flex-column justify-content-between">
                <div>
                  <Row>
                    <div className="col">
                      <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
                        {t("categories.moderation.appealsQueue", "Appeals Queue")}
                      </CardTitle>
                      <span className="h2 font-weight-bold mb-0">
                        {stats.pendingAppeals || 0}
                      </span>
                    </div>
                    <Col className="col-auto">
                      <div className="icon icon-shape bg-gradient-success text-white rounded-circle shadow">
                        <i className="fa fa-balance-scale" />
                      </div>
                    </Col>
                  </Row>
                  
                  <div className="mt-3 text-sm">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="text-muted font-weight-600">{t("categories.moderation.pending", "Pending:")}</span>
                      <Badge color="success" className="badge-md font-weight-700">{stats.appealsPending || 0}</Badge>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="text-muted font-weight-600">{t("categories.moderation.resolvedToday", "Resolved Today:")}</span>
                      <Badge color="neutral" className="badge-md font-weight-700 text-dark border">{stats.appealsResolvedToday || 0}</Badge>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="text-muted font-weight-600">{t("categories.moderation.lastReviewed", "Last Reviewed:")}</span>
                      <span className="font-weight-400 text-xs">
                        {stats.lastAppealReviewedAt ? helpers.getFormattedDate(stats.lastAppealReviewedAt, t) : t("categories.moderation.never", "Never")}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <Button
                    id="view-appeals-queue"
                    color="success"
                    size="sm"
                    className="w-100"
                    title={t("categories.moderation.tooltip.viewAppealsQueue", "View appeals moderation queue")}
                    onClick={() => navigate("/admin/moderation/appeals")}
                  >
                    <span>{t("categories.moderation.viewQueue", "→ View Queue")}</span>
                  </Button>
                  <UncontrolledTooltip target="view-appeals-queue" key={`viewAppealsQueue-tt-${language}`} placement="top" delay={0} container="body">
                    {t("categories.moderation.tooltip.viewAppealsQueue", "View appeals moderation queue")}
                  </UncontrolledTooltip>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Recent Actions Feed */}
        <Row className="mt--4">
          <Col xl="12">
            <Card className="shadow border-0">
              <CardHeader className="bg-white py-3 border-0 d-flex justify-content-between align-items-center">
                <h3 className="mb-0">{t("categories.moderation.recentActions", "Recent Compliance Actions Feed")}</h3>
                <Button size="sm" color="neutral" onClick={refresh}>
                  <i className="fa fa-sync mr-1" /> {t("common.refresh", "Refresh")}
                </Button>
              </CardHeader>
              <CardBody className="pt-0">
                {(!stats.recentActions || stats.recentActions.length === 0) ? (
                  <p className="text-muted text-center py-4">{t("categories.moderation.noRecentActions", "No recent moderation actions recorded.")}</p>
                ) : (
                  <ListGroup className="list-group-flush">
                    {stats.recentActions.map((action, index) => {
                      const flagObj = action.flagId || {};
                      const userStr = flagObj.userId
                        ? `${flagObj.userId.firstName || ""} ${flagObj.userId.lastName || ""}`
                        : "User";

                      let badgeColor = "primary";
                      let badgeLabel = formatStatusLabel(action.action);
                      let actionText = t("categories.moderation.feed.statusChanged", "Status Changed");
                      
                      if (action.action === "created") {
                        badgeColor = "info";
                        badgeLabel = t("categories.moderation.feed.action.created", "Created");
                        actionText = t("categories.moderation.feed.created", "Flag Created for {user}").replace("{user}", userStr);
                      } else if (action.action === "status_changed") {
                        const actionTaken = flagObj.actionTaken || action.newState?.actionTaken || "";
                        if (actionTaken === "warn") {
                          badgeColor = "warning";
                          badgeLabel = t("categories.moderation.feed.action.warn", "Warn");
                          actionText = t("categories.moderation.feed.warned", "Warned {user}").replace("{user}", userStr);
                        } else if (actionTaken === "suspend") {
                          badgeColor = "danger";
                          badgeLabel = t("categories.moderation.feed.action.suspend", "Suspend");
                          actionText = t("categories.moderation.feed.suspended", "Suspended {user}").replace("{user}", userStr);
                        } else if (actionTaken === "restrict") {
                          badgeColor = "warning";
                          badgeLabel = t("categories.moderation.feed.action.restrict", "Restrict");
                          actionText = t("categories.moderation.feed.restricted", "Restricted Chat for {user}").replace("{user}", userStr);
                        } else if (actionTaken === "dismiss") {
                          badgeColor = "success";
                          badgeLabel = t("categories.moderation.feed.action.dismiss", "Dismiss");
                          if (flagObj.flagType === "verification") {
                            actionText = t("categories.moderation.feed.verificationApproved", "Verification Approved for {user}").replace("{user}", userStr);
                          } else {
                            actionText = t("categories.moderation.feed.dismissed", "Flag Dismissed for {user}").replace("{user}", userStr);
                          }
                        } else if (flagObj.flagType === "verification") {
                          const prevDeadline = action.previousState?.fixDeadline || action.previousState?.userDeadline;
                          const newDeadline = action.newState?.fixDeadline || action.newState?.userDeadline;
                          const prevStatus = action.previousState?.status;
                          const newStatus = action.newState?.status;

                          if (prevDeadline && newDeadline && prevDeadline !== newDeadline) {
                            badgeColor = "info";
                            badgeLabel = t("categories.moderation.feed.action.gracePeriodReset", "Grace Period");
                            actionText = t("categories.moderation.feed.gracePeriodReset", "Grace Period Reset for {user}").replace("{user}", userStr);
                          } else if (prevStatus && newStatus && prevStatus !== newStatus) {
                            badgeColor = "info";
                            badgeLabel = formatStatusLabel(newStatus);
                            actionText = t("categories.moderation.feed.verificationStatusChanged", "Verification {newStatus} for {user}").replace("{newStatus}", formatStatusLabel(newStatus)).replace("{user}", userStr);
                          } else {
                            badgeColor = "info";
                            badgeLabel = t("categories.moderation.feed.action.verificationUpdate", "Flag Update");
                            actionText = t("categories.moderation.feed.statusUpdated", "Status Updated for {user}").replace("{user}", userStr);
                          }
                        } else {
                          badgeColor = "warning";
                          badgeLabel = t("categories.moderation.feed.action.statusUpdated", "Updated");
                          actionText = t("categories.moderation.feed.statusUpdated", "Status Updated for {user}").replace("{user}", userStr);
                        }
                      } else if (action.action === "appeal_decision") {
                        badgeColor = "success";
                        const isAccepted = action.notes?.toLowerCase().includes("accept") || flagObj.status === "resolved";
                        badgeLabel = isAccepted
                          ? t("categories.moderation.feed.action.appealAccepted", "Accepted")
                          : t("categories.moderation.feed.action.appealRejected", "Rejected");
                        actionText = isAccepted 
                          ? t("categories.moderation.feed.appealAccepted", "Appeal Accepted for {user}").replace("{user}", userStr)
                          : t("categories.moderation.feed.appealRejected", "Appeal Rejected for {user}").replace("{user}", userStr);
                      }

                      // Format context/reason nicely
                      const rawContext = flagObj.context || "";
                      let displayContext = "";
                      if (rawContext) {
                        if (flagObj.flagType === "verification") {
                          displayContext = helpers.formatRejectionReasons(rawContext, t);
                        } else {
                          displayContext = rawContext.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                        }
                      } else if (flagObj.contentCategory) {
                        displayContext = flagObj.contentCategory.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                      } else if (flagObj.flagType === "verification") {
                        if (action.notes?.toLowerCase().includes("grace period reset") || action.notes?.toLowerCase().includes("deadline")) {
                          displayContext = t("categories.moderation.feed.gracePeriodReset", "Grace Period Reset");
                        } else if (action.previousState?.status && action.newState?.status && action.previousState.status !== action.newState.status) {
                          displayContext = t("categories.moderation.feed.statusChange", "Status: {old} → {new}").replace("{old}", formatStatusLabel(action.previousState.status)).replace("{new}", formatStatusLabel(action.newState.status));
                        } else {
                          displayContext = t("categories.moderation.feed.verificationUpdate", "Verification Flag Update");
                        }
                      } else {
                        displayContext = "Compliance Flag";
                      }

                      return (
                        <ListGroupItem key={action._id || index} className="px-0 py-3 border-light">
                          <div className="d-flex justify-content-between align-items-center">
                            <div style={{ flex: "0 0 80%", paddingRight: "16px" }}>
                              <Badge color={badgeColor} className="mr-2">
                                {badgeLabel}
                              </Badge>
                              <span className="text-sm font-weight-600 text-dark">
                                {actionText}
                              </span>
                              <div className="text-xs text-muted mt-1 font-weight-500">
                                {t("categories.moderation.feed.reasonContext", "Reason/Context:")}
                                {displayContext.includes("; ") ? (
                                  <ul className="mt-1 mb-0" style={{ paddingLeft: "16px", listStyleType: "disc" }}>
                                    {displayContext.split("; ").map((reason, i) => (
                                      <li key={i} className="text-dark font-weight-600" style={{ lineHeight: 1.5 }}>{reason}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <span className="text-dark font-weight-600"> {displayContext}</span>
                                )}
                              </div>
                              {action.notes && (
                                <p className="text-xs text-muted mb-0 mt-1 font-italic">
                                  {t("categories.moderation.feed.notes", "Notes:")} "{helpers.formatNotesDates(action.notes)}"
                                </p>
                              )}
                            </div>
                            <small className="text-muted text-right" style={{ flex: "0 0 20%" }}>
                              {action.createdAt || action.timestamp ? helpers.getFormattedDate(action.createdAt || action.timestamp, t) : "-"}
                            </small>
                          </div>
                        </ListGroupItem>
                      );
                    })}
                  </ListGroup>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

const formatStatusLabel = (str) => {
  if (!str) return "";
  return str
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());
};

export default ModerationDashboard;
